import { useEffect, useMemo, useState } from 'react';
import {
  AddLineItems,
  getProductLineItems,
  ProductCardVariant,
  ProductVariant,
  useAppSelector,
} from '@zonofi/zono-money-store';
import { OrderProductActionType } from '../../constants';
import { TouchableOpacity } from '../../common';
import { getActionLabelByVariant, mapToOrderProduct } from '../helper';
import { onlyDigits } from '@zonofi/common/functions/validation-rules';
import { QuantityEditorCard, SpinnerLoader } from '../..';
import { ReactComponent as DeleteIcon } from '@zonofi/svg-icons/delete.svg';

interface OrderActionsProps {
  variant: ProductCardVariant;
  product: ProductVariant;
  onAddProductPress?: (product: ProductVariant) => void;
  onReplaceProductPress?: (product: ProductVariant) => void;
  onProductRemoveAndUpdate?: (
    product: ProductVariant,
    action: OrderProductActionType
  ) => void;
  lineItems?: AddLineItems[];
  price?: number;
  openUomModal?: () => void;
  currentProductId?: number;
  isOutOfStock?: boolean;
}

export const OrderActions: React.FC<OrderActionsProps> = ({
  variant,
  product,
  product: { productVariantId },
  onAddProductPress,
  onReplaceProductPress,
  onProductRemoveAndUpdate,
  lineItems,
  price,
  openUomModal,
  currentProductId,
  isOutOfStock = false,
}) => {
  const lineItemsFromStore = useAppSelector(getProductLineItems);
  const productLineItems = lineItems ?? lineItemsFromStore ?? [];
  const matchedItem = useMemo(
    () =>
      productLineItems.find(
        (item) => item.productVariantId === productVariantId
      ),
    [productLineItems, productVariantId]
  );

  const productMOQ = matchedItem?.productMOQ ?? 0;
  const [quantity, setQuantity] = useState(matchedItem?.quantity ?? 1);

  useEffect(() => {
    setQuantity(matchedItem?.quantity ?? 0);
  }, [matchedItem?.quantity]);

  const orderProduct = useMemo(() => mapToOrderProduct(product), [product]);

  if (matchedItem) {
    return (
      <div className="product-edit-view">
        <QuantityEditorCard
          quantity={quantity}
          price={price}
          onQuantityChange={(val) =>
            onlyDigits(String(val))?.success
              ? setQuantity(Number(val) || 1)
              : setQuantity((prev) => prev)
          }
          onInputBlur={() => {
            if (quantity !== (matchedItem?.quantity || 1)) {
              onProductRemoveAndUpdate?.(
                { ...product, newQty: quantity },
                OrderProductActionType.EDIT
              );
            }
          }}
          onMinusPress={() => {
            const newQty = quantity - productMOQ;
            if (newQty >= productMOQ) {
              onProductRemoveAndUpdate?.(
                { ...product, newQty },
                OrderProductActionType.EDIT
              );
            }
          }}
          onPlusPress={() => {
            onProductRemoveAndUpdate?.(
              { ...product, newQty: quantity + productMOQ },
              OrderProductActionType.EDIT
            );
          }}
          disable={quantity <= productMOQ}
          disableStepper={isOutOfStock}
          openUomModal={openUomModal}
          product={orderProduct}
        />

        <TouchableOpacity
          onPress={() =>
            onProductRemoveAndUpdate?.(product, OrderProductActionType.REMOVE)
          }
        >
          <DeleteIcon color="#DD360C" width={20} height={20} />
        </TouchableOpacity>
      </div>
    );
  }

  return (
    <TouchableOpacity
      onPress={() => {
        if (isOutOfStock) return;
        variant === ProductCardVariant.ORDER_ADD
          ? onAddProductPress?.(product)
          : onReplaceProductPress?.(product);
      }}
      className={`product-add-card-view ${
        currentProductId === productVariantId ? 'loading' : 'normal'
      } ${variant === ProductCardVariant.ORDER_REPLACE && 'replace-padding'} ${
        isOutOfStock ? 'disabled' : ''
      }`}
    >
      {currentProductId === productVariantId ? (
        <SpinnerLoader color={'#000'} size={18} />
      ) : (
        <span
          className={`product-add-card-view-text font-medium ${
            variant === ProductCardVariant.ORDER_REPLACE && 'replace-text'
          }`}
        >
          {getActionLabelByVariant(variant)}
        </span>
      )}
    </TouchableOpacity>
  );
};
